/**
 * +detail: ground cover for the open zone: wildflower patches in several colours (daisies, poppies,
 * buttercups, lupins, mixed), pebbles, moss cushions, lily pads, tall reeds, mossy logs and stumps,
 * big toadstools and barley rows. Cheap (low segment counts): most of it is walk-through detail
 * merged per chunk and drawn only near the camera.
 *
 * Ids keep the nature prefixes (flowers_, rock_, mossrock_, reeds_, log_, stump_, mushrooms_) so the
 * field builder files them in the outline-free nature chunks.
 */
import * as THREE from 'three';
import { type Gen, combine, paint, registerOutdoorProcs, xf } from '../procs';
import { type RGB3, clamp01, cyl, mix, puff, rgb, tint } from './kit';

const STEM = 0x3f7a2c;
const LEAFY = 0x4f9a3a;

/** Leaf rosettes at the foot of a patch (low-poly: they are tiny). */
function rosettes(rnd: () => number, n: number, spread: number, color = LEAFY): THREE.BufferGeometry[] {
  const out: THREE.BufferGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * spread;
    out.push(paint(xf(puff(0.2, rnd, 0.25, 5, 3, 0.3), [Math.cos(a) * d, 0.04, Math.sin(a) * d], [0, 0, 0], [1.2, 0.4, 0.9]), color, [0.75, 1.08]));
  }
  return out;
}

/** A stem: one open 3-sided prism (6 triangles). */
const stem = (h: number, x: number, z: number, lean: RGB3 = [0, 0, 0]): THREE.BufferGeometry => paint(xf(new THREE.CylinderGeometry(0.012, 0.016, h, 3, 1, true).translate(0, h / 2, 0), [x, 0, z], lean), STEM, [0.8, 1]);
/** A flat flower head facing up (a disc; `n` sides). */
const disc = (r: number, n: number, at: RGB3, tilt: RGB3, color: number, k: readonly [number, number] = [1, 1.08]): THREE.BufferGeometry => paint(xf(new THREE.CircleGeometry(r, n).rotateX(-Math.PI / 2), at, tilt), color, k);

/** Daisies / buttercups: thin stems, a flat disc of petals with a bright centre. */
function daisies(petal: number, centre: number, n0: number, spread: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = n0 + Math.floor(rnd() * 6);
    for (let i = 0; i < n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * spread;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      const h = 0.18 + rnd() * 0.2;
      const tilt: RGB3 = [(rnd() - 0.5) * 0.4, rnd() * 3, (rnd() - 0.5) * 0.4];
      parts.push(stem(h, x, z));
      // Petals: a very flat open cone (reads from above and from the side), the eye a small disc.
      parts.push(paint(xf(new THREE.ConeGeometry(0.085, 0.035, 6, 1, true), [x, h, z], tilt), petal, [1, 1.08]));
      parts.push(disc(0.032, 4, [x, h + 0.02, z], tilt, centre, [1, 1.1]));
    }
    parts.push(...rosettes(rnd, 2, spread * 0.6));
    return combine(parts);
  };
}

/** Poppies / tulips: cup heads on taller stems. */
function cups(colors: readonly number[], n0: number, spread: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = n0 + Math.floor(rnd() * 5);
    for (let i = 0; i < n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * spread;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      const h = 0.3 + rnd() * 0.25;
      parts.push(stem(h, x, z, [(rnd() - 0.5) * 0.25, 0, (rnd() - 0.5) * 0.25]));
      // A cup: an inverted 5-sided cone (petals) with a dark eye.
      parts.push(paint(xf(new THREE.ConeGeometry(0.09, 0.11, 5, 1, true).rotateX(Math.PI), [x, h + 0.05, z]), colors[i % colors.length]!, [0.85, 1.15]));
      parts.push(disc(0.085, 5, [x, h + 0.105, z], [0, 0, 0], colors[i % colors.length]!, [0.7, 0.8]));
      parts.push(disc(0.035, 4, [x, h + 0.11, z], [0, 0, 0], 0x2a2420, [1, 1]));
    }
    parts.push(...rosettes(rnd, 2, spread * 0.6));
    return combine(parts);
  };
}

/** Lupins: tall tapering flower spikes in purples and pinks. */
const lupins: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const colors = [0x7a5ae0, 0xb070e0, 0xe890c8, 0x5a7ae8];
  const n = 5 + Math.floor(rnd() * 4);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.5;
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    const h = 0.45 + rnd() * 0.35;
    const c = rgb(colors[i % colors.length]!);
    parts.push(stem(h * 0.6, x, z));
    const spike = new THREE.ConeGeometry(0.075, h * 0.55, 5, 1, true).translate(x, h * 0.6 + h * 0.27, z);
    parts.push(tint(spike, (_x, y) => mix(c, [1, 1, 1], clamp01((y - h * 0.6) / (h * 0.55)) * 0.35)));
  }
  parts.push(...rosettes(rnd, 2, 0.35, 0x3f8a3a));
  return combine(parts);
};

/** A mixed meadow patch (several colours in one clump). */
const mixed: Gen = (rnd) => combine([daisies(0xffffff, 0xf2c030, 3, 0.55)(rnd), cups([0xff6a4a, 0xffb040], 2, 0.6)(rnd), daisies(0xf8e050, 0xd89a20, 2, 0.5)(rnd)]);

/** Pebbles: a spill of small rounded stones (path verges, stream sides). */
const pebbles: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 5 + Math.floor(rnd() * 5);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.7;
    const r = 0.07 + rnd() * 0.1;
    parts.push(paint(xf(new THREE.OctahedronGeometry(r, 0), [Math.cos(a) * d, r * 0.3, Math.sin(a) * d], [rnd(), rnd(), rnd()], [1.2, 0.6, 1]), i % 3 ? 0x9aa0a8 : 0xb8b4a8, [0.8, 1.08]));
  }
  return combine(parts);
};

/** A moss cushion: a soft, flattened green mound (forest floor, rocks' feet). */
const mossPatch: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const a = rnd() * Math.PI * 2;
    const d = rnd() * 0.45;
    parts.push(paint(xf(puff(0.4 + rnd() * 0.2, rnd, 0.3, 7, 4, 0.2), [Math.cos(a) * d, 0, Math.sin(a) * d], [0, 0, 0], [1.3, 0.28, 1.1]), i % 2 ? 0x5a8a3a : 0x6f9e44, [0.8, 1.1]));
  }
  return combine(parts);
};

/** Lily pads with a flower or two (floats at the water line; local y 0 = the water surface). */
const lilies: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 3 + Math.floor(rnd() * 4);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 1.1;
    const r = 0.22 + rnd() * 0.18;
    const notch = rnd() * Math.PI * 2;
    // A disc with a wedge cut out.
    const pad = new THREE.CircleGeometry(r, 9, notch + 0.35, Math.PI * 2 - 0.7).rotateX(-Math.PI / 2).translate(Math.cos(a) * d, 0.02, Math.sin(a) * d);
    parts.push(paint(pad, i % 2 ? 0x4f9a3a : 0x3f8a40, [0.95, 1.05]));
    if (i < 2 && rnd() < 0.6) {
      const c = i ? 0xf6c0d8 : 0xffffff;
      parts.push(paint(xf(new THREE.ConeGeometry(0.1, 0.12, 6).rotateX(Math.PI), [Math.cos(a) * d, 0.11, Math.sin(a) * d]), c, [1, 1.12]));
      parts.push(paint(xf(new THREE.IcosahedronGeometry(0.035, 0), [Math.cos(a) * d, 0.1, Math.sin(a) * d]), 0xf2d040, [1, 1.1]));
    }
  }
  return combine(parts);
};

/** Tall reeds with cattails (lake shallows, stream banks). */
const reedsTall: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 9 + Math.floor(rnd() * 6);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.6;
    const h = 1.2 + rnd() * 1.0;
    const tilt: RGB3 = [(rnd() - 0.5) * 0.3, 0, (rnd() - 0.5) * 0.3];
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    parts.push(paint(xf(new THREE.ConeGeometry(0.04, h, 3).translate(0, h / 2, 0), [x, 0, z], tilt), i % 3 ? 0x5f8a3a : 0x86a44a, [0.65, 1.12]));
    if (i % 5 === 0) parts.push(paint(xf(cyl(0.05, 0.05, 0.26, 5), [x - tilt[2] * h * 0.8, h * 0.74, z + tilt[0] * h * 0.8]), 0x6a4630, [0.85, 1]));
  }
  return combine(parts);
};

/** A mossy fallen log with branch stubs and a pale cut end. */
const logMossy: Gen = (rnd) => {
  const L = 3.2 + rnd() * 1.6;
  const r = 0.34 + rnd() * 0.12;
  const parts: THREE.BufferGeometry[] = [paint(new THREE.CylinderGeometry(r * 0.9, r, L, 8).rotateZ(Math.PI / 2).translate(0, r * 0.9, 0), 0x6b4a36, [0.55, 1.0])];
  parts.push(paint(xf(new THREE.CylinderGeometry(r * 0.86, r * 0.86, 0.04, 8).rotateZ(Math.PI / 2), [L / 2 + 0.01, r * 0.9, 0]), 0xd8b98a, [1, 1]));
  for (let i = 0; i < 3; i++) parts.push(paint(xf(puff(r * 0.9, rnd, 0.3, 7, 4, 0.5), [(rnd() - 0.5) * L * 0.7, r * 1.65, (rnd() - 0.5) * 0.2], [0, 0, 0], [1.6, 0.4, 1.0]), i % 2 ? 0x5a8a3a : 0x6f9e44, [0.8, 1.1]));
  for (let i = 0; i < 2; i++) parts.push(paint(xf(new THREE.CylinderGeometry(0.06, 0.1, 0.8, 5), [(rnd() - 0.5) * L * 0.6, r * 1.3, 0.2], [0.9, 0, (rnd() - 0.5) * 0.6]), 0x5a3f2a, [0.7, 1]));
  parts.push(paint(xf(new THREE.IcosahedronGeometry(0.1, 0), [L * 0.3, r * 0.4, r + 0.05]), 0xc0463a, [1, 1.1]));
  return combine(parts);
};

/** A mossy stump with a pale cut top, root flare and a toadstool. */
const stumpMossy: Gen = (rnd) => {
  const r = 0.42 + rnd() * 0.2;
  const h = 0.5 + rnd() * 0.4;
  const parts: THREE.BufferGeometry[] = [paint(cyl(r, r * 1.3, h, 8), 0x6b4a36, [0.55, 0.95]), paint(xf(cyl(r * 0.95, r * 0.95, 0.04, 8), [0, h, 0]), 0xd8b98a, [1, 1])];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + rnd();
    parts.push(paint(xf(new THREE.ConeGeometry(0.16, 0.7, 5).rotateZ(Math.PI / 2 - 0.35), [Math.cos(a) * r * 1.1, 0.12, Math.sin(a) * r * 1.1], [0, -a, 0]), 0x5a3f2a, [0.6, 0.9]));
  }
  parts.push(paint(xf(puff(r * 0.8, rnd, 0.3, 7, 4, 0.5), [r * 0.3, h * 0.4, -r * 0.6], [0, 0, 0], [1.2, 0.6, 0.8]), 0x5a8a3a, [0.8, 1.1]));
  parts.push(paint(xf(new THREE.SphereGeometry(0.16, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), [-r - 0.15, 0.18, 0.1]), 0xc0463a, [0.9, 1.1]), paint(xf(cyl(0.04, 0.05, 0.18, 5), [-r - 0.15, 0, 0.1]), 0xeae0c8, [0.8, 1]));
  return combine(parts);
};

/** Big toadstools (shaded dells, the forest floor): fat stems, spotted caps. */
const toadstools: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 2 + Math.floor(rnd() * 3);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = i ? 0.3 + rnd() * 0.3 : 0;
    const s = i ? 0.55 + rnd() * 0.3 : 1;
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    const h = 0.55 * s;
    parts.push(paint(xf(cyl(0.07 * s, 0.1 * s, h, 6), [x, 0, z]), 0xf0e6d0, [0.8, 1]));
    const cap = rnd() < 0.5 ? 0xd8402e : 0xe07a30;
    parts.push(paint(xf(new THREE.SphereGeometry(0.3 * s, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.7, 1), [x, h - 0.03, z]), cap, [0.85, 1.12]));
    for (let k = 0; k < 4; k++) {
      const b = rnd() * Math.PI * 2;
      parts.push(paint(xf(new THREE.IcosahedronGeometry(0.035 * s, 0), [x + Math.cos(b) * 0.17 * s, h + 0.1 * s, z + Math.sin(b) * 0.17 * s]), 0xffffff, [1, 1.1]));
    }
  }
  return combine(parts);
};

/** A 3 yd row of barley (fields: rows of these). */
const cropRow: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 16; i++) {
    const x = -1.5 + (i + rnd() * 0.6) * (3 / 16);
    const z = (rnd() - 0.5) * 0.35;
    const h = 0.7 + rnd() * 0.35;
    const lean: RGB3 = [(rnd() - 0.5) * 0.2, 0, (rnd() - 0.5) * 0.3];
    parts.push(paint(xf(cyl(0.014, 0.02, h, 3), [x, 0, z], lean), 0xb8a85a, [0.75, 1.05]));
    parts.push(paint(xf(new THREE.ConeGeometry(0.05, 0.22, 4).rotateX(Math.PI), [x - lean[2] * h, h + 0.06, z + lean[0] * h], lean), 0xe6cc76, [0.95, 1.2]));
  }
  // A soft green-gold base so the row reads from afar.
  parts.push(paint(xf(puff(0.4, rnd, 0.2, 6, 4, 0.3), [0, 0.12, 0], [0, 0, 0], [4.2, 0.45, 0.8]), 0x9a9c4a, [0.8, 1.05]));
  return combine(parts);
};

/** A slab-like grey boulder with a moss cap (meadow scatter; varied sizes through the scale). */
const boulderSlab: Gen = (rnd) => {
  const g = new THREE.IcosahedronGeometry(1, 1);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const seen = new Map<string, number>();
  for (let i = 0; i < p.count; i++) {
    const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
    let k = seen.get(key);
    if (k === undefined) seen.set(key, (k = 0.8 + rnd() * 0.35));
    p.setXYZ(i, p.getX(i) * k * 1.3, Math.max(-0.1, p.getY(i) * k * 0.6), p.getZ(i) * k);
  }
  g.translate(0, 0.1, 0);
  const dark = rgb(0x5f6c80);
  const mid = rgb(0x8a96a8);
  const moss = rgb(0x5f8a3e);
  const out = tint(g, (_x, y, _z, _nx, ny) => (ny > 0.62 && y > 0.25 ? mix(moss, [0.5, 0.62, 0.3], clamp01(ny - 0.62)) : mix(dark, mid, clamp01(y / 0.7))), true);
  return out;
};

registerOutdoorProcs({
  flowers_daisy: daisies(0xffffff, 0xf2c030, 9, 0.7),
  flowers_butter: daisies(0xf8dc40, 0xd89a20, 9, 0.65),
  flowers_poppy: cups([0xff5a3a, 0xff7a40, 0xe83a3a], 8, 0.7),
  flowers_tulip: cups([0xf2a0c8, 0xffffff, 0xf8d050], 7, 0.6),
  flowers_lupin: lupins,
  flowers_mixed: mixed,
  rock_pebbles: pebbles,
  mossrock_patch: mossPatch,
  reeds_lily: lilies,
  reeds_tall: reedsTall,
  log_mossy: logMossy,
  stump_mossy: stumpMossy,
  mushrooms_big: toadstools,
  barley_row: cropRow,
  boulder_slab: boulderSlab,
});
